import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
} from "reactstrap";
import { MoreHorizontal } from "lucide-react"; // For dropdown icon if needed (or use Check for selection)
import SubscriptionCards from "./SubscriptionCards";
import { getPaymentMethods } from "../../../api-handle/paymentApi";

const BuyNowModal = ({ isOpen, toggle, selectedPlan, onProceedCheckout }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [paymentError, setPaymentError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch payment methods when modal opens or selectedPlan changes
    const fetchPaymentMethods = async () => {
      try {
        const response = await getPaymentMethods(selectedPlan);
        if (response.isSuccess && response.paymentMethods) {
          setPaymentMethods(response.paymentMethods);
          // Optionally select the first method by default
          if (response.paymentMethods.length > 0) {
            setSelectedPaymentMethodId(
              response.paymentMethods[0].PaymentMethodId,
            );
          }
        } else {
          setPaymentError("Failed to load payment methods");
        }
      } catch (error) {
        setPaymentError("Failed to load payment methods");
      }
    };

    if (isOpen && selectedPlan) {
      fetchPaymentMethods();
    } else {
      setPaymentMethods([]);
      setSelectedPaymentMethodId(null);
      setPaymentError("");
    }
  }, [isOpen, selectedPlan]);

  const handleCheckout = async () => {
    if (loading) return;
    if (!selectedPaymentMethodId) {
      setPaymentError("Please select a payment method.");
      return;
    }

    setLoading(true);
    setPaymentError("");

    try {
      // Parent will redirect on success; if it throws, modal stays open
      // so the user can retry without losing their payment-method selection.
      await onProceedCheckout(selectedPlan, selectedPaymentMethodId);
    } catch (error) {
      setPaymentError(error?.message || "Payment failed. Please try again.");
      setLoading(false);
    }
  };

  const plans = [selectedPlan]; // just one plan wrapped in array

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Checkout with MyFatoorah</ModalHeader>
      <ModalBody>
        <div className="d-flex flex-column flex-md-row gap-4">
          {/* Left Side: Plan Details */}
          <div
            style={{
              border: "1px solid #33bdbf",
              borderRadius: "15px",
              padding: "1rem",
            }}
          >
            <SubscriptionCards plan={plans} />
          </div>

          {/* Right Side: Payment Method Selection */}
          <div className="w-100">
            <Form>
              {paymentError && <Alert color="danger">{paymentError}</Alert>}

              <FormGroup>
                <Label for="paymentMethod">Select Payment Method</Label>
                {paymentMethods.length === 0 && !paymentError && (
                  <Spinner size="sm" />
                )}
                {paymentMethods.map((method) => (
                  <div
                    key={method.PaymentMethodId}
                    className="d-flex align-items-center mb-2"
                  >
                    <Input
                      type="radio"
                      name="paymentMethod"
                      id={`paymentMethod-${method.PaymentMethodId}`}
                      checked={
                        selectedPaymentMethodId === method.PaymentMethodId
                      }
                      onChange={() =>
                        setSelectedPaymentMethodId(method.PaymentMethodId)
                      }
                    />
                    <Label
                      for={`paymentMethod-${method.PaymentMethodId}`}
                      className="mb-0 ms-2 d-flex align-items-center"
                      style={{ cursor: "pointer" }}
                    >
                      <img
                        src={method.ImageUrl}
                        alt={method.PaymentMethodEn}
                        style={{ height: 24, marginRight: 8 }}
                      />
                      {method.PaymentMethodEn}
                    </Label>
                  </div>
                ))}
              </FormGroup>

              <Alert color="info" className="mt-3">
                You will be redirected to MyFatoorah to complete the payment.
              </Alert>
            </Form>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleCheckout} disabled={loading}>
          {loading ? <Spinner size="sm" /> : "Proceed to MyFatoorah"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BuyNowModal;
